import React, { useState, useEffect } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';

export interface OrgInputOption {
  label: string;
  value: string;
}

interface CompanyNameToOrgNumber {
  [companyName: string]: string;
}

interface OrgInputTextFieldProps {
  disabled: boolean;
  textFieldProps: TextFieldProps;
  onValidOrgNumberEntered: (value: string) => void;
  onInvalidOrgNumberEntered: () => void;
  onOptionSelected?: (option: OrgInputOption | null) => void;
}


const isOrgNumber = (value: string) => {
  const orgNumberRegex = /^\d{6}[-]?\d{4}$/;
  return orgNumberRegex.test(value.trim());
}

const sanitizeOrgNumber = (value: string) => {
  const sanitizedValue = value.replace(/[\s-]/g, '');
  return sanitizedValue;
}

const OrgInputTextField = (props: OrgInputTextFieldProps) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<OrgInputOption[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setOptions([]);
    setLoading(false);
    const fetchOptions = async () => {
      if (searchValue.trim() === '' || searchValue.length < 3) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const data = await api.get<CompanyNameToOrgNumber>(`/v1/companies?prefix=${encodeURIComponent(searchValue)}`);

        const mappedOptions: OrgInputOption[] = Object.entries(data).map(([name, orgNumber]) => ({
          label: name,
          value: orgNumber
        }));

        if (active) setOptions(mappedOptions);
      } catch (error) {
        console.error('Error fetching options:', error);
      } finally {
        if (active) setLoading(false);
      }
    };

    const debounceTimeout = setTimeout(fetchOptions, 300);
    return () => { active = false; clearTimeout(debounceTimeout); };
  }, [searchValue]);

  const onInputChange = (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    // Selecting an option updates the input with its label; onChange owns that selection.
    if (reason === 'reset') return;
    if (reason === 'selectOption') {
      // The user has selected an option - get the associated org number
      const selectedOrg = options.find(option => option.label === value);
      if (selectedOrg) {
        props.onValidOrgNumberEntered(selectedOrg.value);
        props.onOptionSelected?.(selectedOrg);
        return;
      }
    } else {
      if (isOrgNumber(value)) {
        setSearchValue('');
        setOptions([]);
        setLoading(false);
        const sanitizedValue = sanitizeOrgNumber(value);
        props.onValidOrgNumberEntered(sanitizedValue);
        props.onOptionSelected?.(null);
      } else {
        props.onInvalidOrgNumberEntered();
        props.onOptionSelected?.(null);
        setSearchValue(value);
      }
    }
  }

  return (
    <Autocomplete
      sx={{ minWidth: 0, width: 300, maxWidth: '100%' }}
      disabled={props.disabled}
      options={options}
      freeSolo={true}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
      onInputChange={onInputChange}
      onChange={(_event, option) => {
        if (option && typeof option !== 'string') {
          props.onValidOrgNumberEntered(option.value);
          props.onOptionSelected?.(option);
          setSearchValue('');
        }
      }}
      loading={loading}
      loadingText={t('common.states.loading')}
      filterOptions={(x) => x}
      autoComplete={true}
      renderInput={(params) => (
        <TextField
          {...props.textFieldProps}
          {...params}
          size="small"
          type="text"
          disabled={props.disabled}
          InputProps={{ ...params.InputProps, endAdornment: <>{loading && <CircularProgress size={16} color="inherit" aria-label={t('common.states.loading')} />}{params.InputProps.endAdornment}</> }}
          sx={{
            width: '100%',
            ...(props.textFieldProps.sx as any)
          }}
        />
      )}
    />
  );
};

export default OrgInputTextField;
